import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as Dialog from '@radix-ui/react-dialog';
import { ModuleHeader } from '@/components/ui/module-header';
import { DataTable } from '@/components/ui/data-table';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { StatusPill } from '@/components/ui/status-pill';
import { FloatingField } from '@/components/ui/floating-field';
import { MoneyField, sanitizeMoneyInput } from '@/components/ui/money-field';
import { DateField } from '@/components/ui/date-field';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { formatCurrency } from '@/lib/utils';
import * as Icons from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';

const CATEGORIES = [
  { value: 'fuel', label: 'Fuel' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'lumper', label: 'Lumper' },
  { value: 'tolls', label: 'Tolls' },
  { value: 'lease', label: 'Lease' },
  { value: 'other', label: 'Other' },
];

function toYmd(d) {
  if (!d) return '';
  if (typeof d === 'string') return d.slice(0, 10);
  const date = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

const emptyBillForm = { vendor: '', category: 'fuel', amount: '', billNumber: '', due: null, description: '' };

export default function APPage() {
  const qc = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState('');
  const [deleteId, setDeleteId] = useState('');
  const [form, setForm] = useState(emptyBillForm);
  const [editForm, setEditForm] = useState(emptyBillForm);
  const [partialPay, setPartialPay] = useState(null); // { id, remainingCents, amount }

  const billsQ = useQuery({
    queryKey: ['ap-bills'],
    queryFn: async () => {
      const r = await api('/api/v1/ap/bills');
      if (r.status !== 200) throw new Error(r.data?.error ?? 'Failed to load bills');
      return {
        bills: r.data.bills ?? [],
        openTotalCents: r.data.openTotalCents ?? 0,
      };
    },
  });

  useEffect(() => {
    if (billsQ.isError) toast.error(billsQ.error?.message ?? 'Failed to load AP');
  }, [billsQ.isError, billsQ.error]);

  const bills = billsQ.data?.bills ?? [];
  const openTotal = billsQ.data?.openTotalCents ?? 0;
  const editingBill = bills.find((b) => b.id === editingId);
  const deleteTarget = bills.find((b) => b.id === deleteId);

  const recomputeOpenTotal = (list) =>
    (list || []).reduce((sum, b) => {
      if (b.status === 'void' || b.status === 'paid') return sum;
      return sum + Math.max(0, (b.amountCents || 0) - (b.amountPaidCents || 0));
    }, 0);

  const upsertBill = (bill) => {
    qc.setQueryData(['ap-bills'], (prev) => {
      if (!prev) return prev;
      const list = Array.isArray(prev.bills) ? prev.bills : [];
      const next = list.some((b) => b.id === bill.id)
        ? list.map((b) => (b.id === bill.id ? { ...b, ...bill } : b))
        : [bill, ...list];
      return { ...prev, bills: next, openTotalCents: recomputeOpenTotal(next) };
    });
  };

  const handleAdd = async () => {
    if (!form.vendor || !form.category || !form.amount) {
      toast.error('Vendor, category, and amount are required');
      return;
    }
    if (!toYmd(form.due)) {
      toast.error('Due date is required');
      return;
    }
    const amountCents = Math.round((parseFloat(form.amount) || 0) * 100);
    const r = await api('/api/v1/ap/bills', {
      method: 'POST',
      body: {
        vendorName: form.vendor.trim(),
        category: form.category,
        amountCents,
        billNumber: form.billNumber || undefined,
        description: form.description || undefined,
        dueDate: toYmd(form.due),
      },
    });
    if (r.status === 201) {
      toast.success('Bill created');
      if (r.data?.bill) upsertBill(r.data.bill);
      setForm(emptyBillForm);
      setShowAdd(false);
    } else {
      toast.error(String(r.data?.error ?? 'Failed'));
    }
  };

  const openEdit = (bill) => {
    setEditingId(bill.id);
    setEditForm({
      vendor: bill.vendorName ?? '',
      category: bill.category ?? 'other',
      amount: bill.amountCents ? String((bill.amountCents / 100).toFixed(2)) : '',
      billNumber: bill.billNumber ?? '',
      due: bill.dueDate ? new Date(bill.dueDate) : null,
      description: bill.description ?? '',
    });
  };

  const handleSaveEdit = async () => {
    if (!editingId) return;
    const amountCents = Math.round((parseFloat(editForm.amount) || 0) * 100);
    const r = await api(`/api/v1/ap/bills/${editingId}`, {
      method: 'PATCH',
      body: {
        vendor: editForm.vendor.trim(),
        category: editForm.category,
        amountCents: amountCents || undefined,
        billNumber: editForm.billNumber || undefined,
        description: editForm.description || undefined,
        dueDate: toYmd(editForm.due) || undefined,
      },
    });
    if (r.status === 200) {
      toast.success('Bill updated');
      if (r.data?.bill) upsertBill(r.data.bill);
      setEditingId('');
    } else {
      toast.error(String(r.data?.error ?? 'Failed'));
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    const id = deleteId;
    const r = await api(`/api/v1/ap/bills/${id}`, { method: 'DELETE' });
    if (r.status === 200) {
      toast.success('Bill deleted');
      setDeleteId('');
      qc.setQueryData(['ap-bills'], (prev) => {
        if (!prev) return prev;
        const next = (prev.bills ?? []).filter((b) => b.id !== id);
        return { ...prev, bills: next, openTotalCents: recomputeOpenTotal(next) };
      });
    } else {
      toast.error(String(r.data?.error ?? 'Failed'));
    }
  };

  const submitPayment = async (id, amountCents) => {
    const r = await api(`/api/v1/ap/bills/${id}/pay`, {
      method: 'POST',
      body: amountCents == null ? {} : { amountCents },
    });
    if (r.status === 200) {
      const bal = r.data?.balanceCents;
      toast.success(
        bal != null ? `Payment recorded — remaining ${formatCurrency(bal / 100)}` : 'Payment recorded'
      );
      if (r.data?.bill) upsertBill(r.data.bill);
      return true;
    }
    toast.error(String(r.data?.error ?? 'Failed'));
    return false;
  };

  const pay = async (id, full = true, balanceHint) => {
    const remaining = Number(balanceHint) || 0;
    if (full) {
      await submitPayment(id, null);
      return;
    }
    const dollars = remaining > 0 ? (remaining / 100).toFixed(2) : '';
    setPartialPay({ id, remainingCents: remaining, amount: dollars });
  };

  const confirmPartialPay = async () => {
    if (!partialPay?.id) return;
    const cleaned = sanitizeMoneyInput(partialPay.amount);
    if (!cleaned || cleaned === '.') {
      toast.error('Enter a payment amount using numbers only');
      return;
    }
    const dollars = Number(cleaned);
    if (!Number.isFinite(dollars) || dollars <= 0) {
      toast.error('Amount must be a positive number');
      return;
    }
    const amountCents = Math.round(dollars * 100);
    if (amountCents <= 0) {
      toast.error('Amount must be a positive number');
      return;
    }
    if (partialPay.remainingCents > 0 && amountCents > partialPay.remainingCents) {
      toast.error(
        `Payment exceeds remaining ${formatCurrency(partialPay.remainingCents / 100)}`
      );
      return;
    }
    const ok = await submitPayment(partialPay.id, amountCents);
    if (ok) setPartialPay(null);
  };

  const columns = [
    {
      key: 'billNumber',
      label: 'Bill #',
      className: 'font-mono',
      render: (v, row) => v || row.id?.slice(0, 8),
    },
    { key: 'vendorName', label: 'Vendor', sortable: true },
    {
      key: 'category',
      label: 'Category',
      render: (v) => <StatusPill status={v || 'other'} />,
    },
    {
      key: 'amountCents',
      label: 'Amount',
      render: (v) => <span className="font-mono">{formatCurrency((v || 0) / 100)}</span>,
    },
    {
      key: 'amountPaidCents',
      label: 'Paid',
      render: (v) => <span className="font-mono">{formatCurrency((v || 0) / 100)}</span>,
    },
    {
      key: 'remaining',
      label: 'Remaining',
      render: (_, row) => {
        const rem = Math.max(0, (row.amountCents || 0) - (row.amountPaidCents || 0));
        return <span className="font-mono">{formatCurrency(rem / 100)}</span>;
      },
    },
    {
      key: 'dueDate',
      label: 'Due Date',
      render: (v) => v || '—',
    },
    { key: 'status', label: 'Status', render: (v) => <StatusPill status={v} /> },
    {
      key: 'actions',
      label: '',
      render: (_, row) => {
        const remaining = Math.max(0, (row.amountCents || 0) - (row.amountPaidCents || 0));
        return (
        <div className="flex gap-1 flex-wrap justify-end">
          {(row.status === 'open' || row.status === 'partial') && (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={() => pay(row.id, true, remaining)}
              >
                Pay Full
              </Button>
              <Button size="sm" variant="ghost" onClick={() => pay(row.id, false, remaining)}>
                Partial
              </Button>
              <Button size="sm" variant="ghost" onClick={() => openEdit(row)}>
                <Icons.Pencil className="h-3 w-3 mr-1" />
                Edit
              </Button>
            </>
          )}
          {row.status !== 'paid' && (
            <Button
              size="sm"
              variant="ghost"
              className="text-destructive hover:text-destructive"
              onClick={() => setDeleteId(row.id)}
            >
              <Icons.Trash2 className="h-3 w-3" />
            </Button>
          )}
          </div>
        );
      },
    },
  ];

  return (
<div className="space-y-6 motion-enter">
        <ModuleHeader
          title="Accounts Payable"
          description="Manage vendor bills and payments"
          icon="Receipt"
          metrics={[
            { label: 'Total', value: bills.length },
            {
              label: 'Open $',
              value: formatCurrency(openTotal / 100),
            },
          ]}
          actions={
            <Button onClick={() => setShowAdd(!showAdd)}>
              <Icons.Plus className="h-4 w-4 mr-2" />
              Create Bill
            </Button>
          }
        />

        {showAdd && (
          <Card>
            <CardHeader>
              <CardTitle>New Bill</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FloatingField
                  label="Vendor Name"
                  value={form.vendor}
                  onChange={(e) => setForm({ ...form, vendor: e.target.value })}
                  required
                />
                <div className="space-y-2">
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Category *
                  </label>
                  <Select
                    value={form.category}
                    onValueChange={(v) => setForm({ ...form, category: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => (
                        <SelectItem key={c.value} value={c.value}>
                          {c.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <FloatingField
                  label="Amount ($)"
                  type="number"
                  step="0.01"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  required
                />
                <FloatingField
                  label="Vendor Bill #"
                  value={form.billNumber}
                  onChange={(e) => setForm({ ...form, billNumber: e.target.value })}
                />
                <div className="space-y-2">
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Due Date <span className="text-destructive">*</span>
                  </label>
                  <DateField value={form.due} onChange={(d) => setForm({ ...form, due: d })} />
                </div>
                <div className="md:col-span-2">
                  <FloatingField
                    label="Description"
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowAdd(false)}>
                  Cancel
                </Button>
                <Button onClick={handleAdd}>Create Bill</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {editingId && editingBill && (
          <Card>
            <CardHeader>
              <CardTitle>Edit Bill — {editingBill.vendorName}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FloatingField
                  label="Vendor Name"
                  value={editForm.vendor}
                  onChange={(e) => setEditForm({ ...editForm, vendor: e.target.value })}
                  required
                />
                <div className="space-y-2">
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Category
                  </label>
                  <Select value={editForm.category} onValueChange={(v) => setEditForm({ ...editForm, category: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <FloatingField
                  label="Amount ($)"
                  type="number"
                  step="0.01"
                  value={editForm.amount}
                  onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })}
                />
                <FloatingField
                  label="Vendor Bill #"
                  value={editForm.billNumber}
                  onChange={(e) => setEditForm({ ...editForm, billNumber: e.target.value })}
                />
                <div className="space-y-2">
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Due Date</label>
                  <DateField value={editForm.due} onChange={(d) => setEditForm({ ...editForm, due: d })} />
                </div>
                <div className="md:col-span-2">
                  <FloatingField
                    label="Description"
                    value={editForm.description}
                    onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEditingId('')}>Cancel</Button>
                <Button onClick={handleSaveEdit}>Save changes</Button>
              </div>
            </CardContent>
          </Card>
        )}

        <DataTable
          columns={columns}
          data={bills}
          emptyMessage={billsQ.isLoading ? 'Loading…' : 'No vendor bills'}
        />

        <ConfirmDialog
          open={Boolean(deleteId)}
          onOpenChange={(open) => !open && setDeleteId('')}
          title="Delete bill?"
          description={`Delete bill from ${deleteTarget?.vendorName || 'vendor'}? Only non-paid bills can be deleted.`}
          confirmLabel="Delete"
          variant="destructive"
          onConfirm={handleDelete}
        />

        <Dialog.Root
          open={Boolean(partialPay)}
          onOpenChange={(open) => !open && setPartialPay(null)}
        >
          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
            <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg bg-card p-6 shadow-lg">
              <Dialog.Close className="absolute right-4 top-4">
                <Icons.X className="h-4 w-4" />
              </Dialog.Close>
              <Dialog.Title className="text-lg font-semibold mb-1">Partial payment</Dialog.Title>
              <Dialog.Description className="text-sm text-muted-foreground mb-4">
                Remaining balance:{' '}
                {formatCurrency((partialPay?.remainingCents || 0) / 100)}. Numbers only — no
                letters, negatives, or symbols.
              </Dialog.Description>
              <MoneyField
                label="Amount ($)"
                value={partialPay?.amount ?? ''}
                onChange={(e) => {
                  let next = sanitizeMoneyInput(e.target.value);
                  const dot = next.indexOf('.');
                  if (dot !== -1) next = next.slice(0, dot + 1) + next.slice(dot + 1, dot + 3);
                  setPartialPay((prev) => (prev ? { ...prev, amount: next } : prev));
                }}
              />
              <div className="mt-6 flex justify-end gap-3">
                <Button variant="outline" onClick={() => setPartialPay(null)}>
                  Cancel
                </Button>
                <Button onClick={confirmPartialPay}>Record payment</Button>
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      </div>
);
}
